import { lazy, Suspense } from 'react';
import { GamePhase, type GameView } from '@secretnenshi/shared';
import { ReclaimRequests } from '../components/chrome';
import { Button, Page } from '../components/ui';
import { ActionPanel } from '../game/ActionPanel';
import { Offices, PlayerList, Tracks } from '../game/Board';
import { MyIdentity } from '../game/Identity';
import { Record } from '../game/Record';
import { leaveGame, play } from '../state/actions';
import { useStore } from '../state/store';

// Loaded only when a game ends: card styles, poster font and portraits.
const GameOver = lazy(() => import('../game/GameOver'));

/**
 * Standard gameplay screen (spec §43). Every element here is driven by public
 * state, so all players' screens look the same (spec §42, §78). The only
 * private information is behind the My identity hold control.
 */
export function Game({ view }: { view: GameView }) {
  const me = useStore((s) => s.session?.playerId);
  const busy = useStore((s) => s.busy);
  const game = view.game!;
  const isHost = view.hostPlayerId === me;

  if (game.phase === GamePhase.GAME_OVER) {
    return (
      <Page>
        <Suspense fallback={<p className="hint">The results are being certified…</p>}>
          <GameOver view={view} />
        </Suspense>
      </Page>
    );
  }

  return (
    <Page compact>
      <div className="game-head">
        <span>Game {view.code}</span>
        <span>Round {game.turnNumber}</span>
      </div>

      {isHost ? <ReclaimRequests /> : null}
      {view.paused ? (
        <div className="banner" role="status">
          <p className="banner-title">Parliament is prorogued</p>
          <p>The host has paused the game.</p>
        </div>
      ) : null}

      <Offices view={view} />
      <ActionPanel view={view} />
      <Tracks game={game} />

      <section className="section">
        <h2 className="section-label">Players</h2>
        <PlayerList view={view} me={me} />
      </section>

      <Record view={view} />

      <div className="stack">
        {isHost ? (
          <Button variant="quiet" disabled={busy} onClick={() => void (view.paused ? play.resume() : play.pause())}>
            {view.paused ? 'Resume game' : 'Pause game'}
          </Button>
        ) : null}
        <Button variant="quiet" disabled={busy} onClick={() => void leaveGame()}>
          Leave for now
        </Button>
      </div>

      {game.phase !== GamePhase.ROLE_REVEAL ? (
        <div className="dock">
          <MyIdentity />
        </div>
      ) : null}
    </Page>
  );
}
